/**
 * The portal navbar's "More" group — which tabs fold into it and the pure
 * split the navbar applies after scope filtering.
 *
 * Usage-graph finding C7 (gurucloudai kanban 5a2a142b, ChemMasters card
 * 4bc8579e): over 90 days to 2026-10-05 Orders drew 1,390 visits and Inbox
 * 1,019, while Pricing exemptions drew 29, My roadmap 24, Quality Signals 21
 * and Inbox analytics 18. Those four leave the top row and sit one click away
 * under "More". Nothing is removed: every route, guard and scope gate is
 * untouched; this only decides where the link is drawn.
 *
 * Rare is not the same as unimportant — the owner confirms this list in the
 * merge review, and changing it is a one-line edit here.
 *
 * The Merlin family entry (constants/merlins.ts) is NOT a tab in this list:
 * Merlin Analyst already collapses into the branded Merlin switcher, so it
 * is grouped by that mechanism rather than this one.
 */

/** Tab ids (PortalNavbar allTabs) that render inside the "More" menu. */
export const MORE_MENU_TAB_IDS: readonly string[] = [
  'pricing-exemptions',
  'signals',
  'inbox-analytics',
  'my-roadmap',
  // Proposal 11 (kanban 2a28d4c0): the person's open quality asks.
  'questions-for-me',
  // Missed Orders (kanban fc6bca28): the Alpha team's correction loop, rare by design.
  'missed-orders',
];

export interface MoreMenuSplit<T extends { id: string }> {
  /** Tabs drawn on the top row, in their original order. */
  primary: T[];
  /** Tabs drawn inside "More", in their original order (empty = no menu). */
  more: T[];
}

/**
 * Split the (already scope-filtered) tabs into the top row and the "More"
 * group. Order is preserved on both sides, so a tab keeps its place relative
 * to its neighbours whichever side it lands on.
 */
export function splitMoreMenu<T extends { id: string }>(
  tabs: readonly T[],
  moreIds: readonly string[] = MORE_MENU_TAB_IDS,
): MoreMenuSplit<T> {
  const primary: T[] = [];
  const more: T[] = [];
  for (const tab of tabs) {
    (moreIds.includes(tab.id) ? more : primary).push(tab);
  }
  return { primary, more };
}

